import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const STACK = [
  "Power BI", "Tableau", "Qlik", "Snowflake", "Sisense",
  "MicroStrategy", "Zoho Analytics", "Microsoft SQL Server", "Oracle",
];

const SERVICES = [
  { n: "01", t: "Database Management", d: "Architecture, performance tuning, and migration across Oracle, MS SQL, and Snowflake." },
  { n: "02", t: "Data Pipeline Engineering", d: "Production-grade ELT/ETL pipelines that move trustworthy data at scale." },
  { n: "03", t: "BI Dashboards", d: "Power BI, Tableau, Qlik, Sisense and MicroStrategy dashboards built for decisions." },
  { n: "04", t: "Analytics Strategy", d: "Roadmaps that align data platforms with quarterly business outcomes." },
];

const Home = () => (
  <>
    {/* HERO */}
    <section className="relative bg-[#0a0a0a] text-white overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-[#0055FF]/20 blur-[180px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 lg:pt-36 pb-24 lg:pb-32 relative">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-3 mb-8 fade-up">
              <span className="w-2 h-2 bg-[#0055FF]"></span>
              <span className="text-xs tracking-[0.25em] uppercase text-white/60">
                Data & BI Analytics · Contract Engagements
              </span>
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display text-[clamp(3rem,8.5vw,9rem)] leading-[0.88] tracking-tighter fade-up"
            >
              Decisions, <br />
              <span className="text-white/60">engineered from</span> <br />
              raw data<span className="text-[#0055FF]">.</span>
            </h1>
          </div>

          <div className="col-span-12 lg:col-span-4 lg:pl-8 lg:border-l lg:border-white/10 fade-up">
            <p className="text-base lg:text-lg text-white/70 leading-relaxed mb-8 max-w-md">
              Pragma Analytics Group is a senior consulting team
              delivering end-to-end data engineering and BI dashboards
              for Fortune 1000 leaders.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-primary">
                <Button className="bg-[#0055FF] hover:bg-[#0044cc] text-white rounded-none h-12 px-6 font-medium">
                  Start a contract <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-black rounded-none h-12 px-6 font-medium">
                  Explore services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* TECH MARQUEE */}
    <section className="bg-[#0a0a0a] py-12 border-y border-white/10 overflow-hidden" data-testid="tech-marquee">
      <div className="flex items-center mb-6 max-w-[1400px] mx-auto px-6 lg:px-12">
        <span className="text-xs uppercase tracking-widest text-white/40">Platforms we operate</span>
      </div>
      <div className="relative w-full overflow-hidden">
        <div className="flex gap-16 md:gap-24 animate-marquee whitespace-nowrap w-max">
          {[...STACK, ...STACK].map((s, i) => (
            <span key={i} className="font-display text-3xl md:text-5xl tracking-tighter text-white/50 hover:text-white transition-colors">
              {s}
            </span>
          ))}
        </div>
      </div>
    </section>

    {/* SERVICES grid (Control Room) */}
    <section className="bg-[#0a0a0a] text-white" data-testid="home-services">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-5">
            <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-4">Capabilities</p>
            <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95]">
              Four practice areas. One outcome.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-white/60 text-base leading-relaxed">
              Each engagement is a fixed-scope contract delivered by senior
              architects. No staff augmentation, no juniors on critical paths.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/10">
          {SERVICES.map((s) => (
            <div
              key={s.n}
              className="group p-8 lg:p-12 border-r border-b border-white/10 hover:bg-white/[0.02] transition-colors"
              data-testid={`home-service-${s.n}`}
            >
              <div className="flex items-start justify-between mb-10">
                <span className="text-xs tracking-widest text-white/40">{s.n}</span>
                <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-[#0055FF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-4">{s.t}</h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-md">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link to="/services" data-testid="home-services-view-all">
            <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-black rounded-none h-12 px-6 font-medium">
              View all services <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* EDITORIAL LIGHT SECTION */}
    <section className="bg-white text-[#09090b]" data-testid="home-editorial">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <p className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-6">Methodology</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.92] mb-10">
              We don't sell tools. <br />
              We <span className="text-[#0055FF]">ship</span> measurable <br />outcomes.
            </h2>
            <p className="text-zinc-600 text-lg leading-relaxed max-w-xl">
              Pragma operates in 4-week sprints with a fixed deliverable
              contract. Each sprint produces a production-deployed artifact:
              a pipeline, a dashboard, a model, or an integration.
            </p>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="border-t border-black/10">
              {[
                { k: "01", v: "Discovery & data audit", d: "Week 1" },
                { k: "02", v: "Architect & contract", d: "Week 2" },
                { k: "03", v: "Build in production", d: "Week 3" },
                { k: "04", v: "Handover & enablement", d: "Week 4" },
              ].map((p) => (
                <div key={p.k} className="flex items-baseline gap-6 py-6 border-b border-black/10">
                  <span className="text-xs text-zinc-400 tabular-nums">{p.k}</span>
                  <div className="flex-1">
                    <p className="font-display text-2xl tracking-tight">{p.v}</p>
                  </div>
                  <span className="text-xs text-zinc-400 tracking-wider uppercase">{p.d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default Home;

